import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { chatAttachmentQueryOptions } from "web_myplann/Api/Chat/useChatAttachmentQuery";
import { handleDocumentOnDevice } from "web_myplann/Api/Document/handleDocumentOnDevice";
import { getFileNameWithExtension } from "web_myplann/Chat/Helpers/Attachments/getFileNameWithExtension";
import { ApiCallError } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { blobToBase64 } from "web_myplann/Infrastructure/Utils/FileUtils";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";
import { HandleDocumentTypes } from "web_myplann/Utils/WebViewModel";

export type HandleChatAttachmentVariables = {
	attachmentId: number;
	fileName: string;
	mimeType: string;
	actionType: keyof typeof HandleDocumentTypes;
};

export const useHandleChatAttachmentMutation = () => {
	const session = useAtomValue(sessionState);
	const queryClient = useQueryClient();
	const { t } = useResource();

	return useMutation({
		mutationFn: async ({
			attachmentId,
			fileName,
			mimeType,
			actionType,
		}: HandleChatAttachmentVariables): Promise<void> => {
			const errorMessage = t(
				actionType === HandleDocumentTypes.SHARE
					? Resources.Chat.attachmentShareFailed
					: Resources.Chat.attachmentOpenFailed,
			);

			let blob: Blob;
			try {
				blob = await queryClient.fetchQuery(
					chatAttachmentQueryOptions(attachmentId, session),
				);
			} catch (error) {
				if (error instanceof ApiCallError && error.data.status === 401) {
					throw error;
				}
				throw new Error(errorMessage, { cause: error });
			}

			await handleDocumentOnDevice({
				base64: await blobToBase64(blob),
				filename: getFileNameWithExtension(fileName, mimeType),
				mimeType,
				actionType,
				errorMessage,
			});
		},
	});
};
